import { useOrgAuthStore, useOrgMembers } from "@solomon-apps/shared";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import {
    InputGroup,
    InputGroupAddon,
    InputGroupInput
} from "@solomon-apps/shared/src/components/ui/input-group";
import { Skeleton } from "@solomon-apps/shared/src/components/ui/skeleton";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import { SearchIcon } from "@solomon-labs/design/components/icons";
import { TabsMenu, TabTrigger } from "@solomon-labs/design/components/ui";
import { motion, useReducedMotion } from "motion/react";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CLIENT_OAUTH_CONFIG } from "@/constants/oauthConfig";
import { resolveClientFormError } from "@/utils/clientFormErrors";

import {
    type MemberRow,
    MembersTable,
    MembersTableSkeleton
} from "./Team/components/MembersTable";
import {
    countByStatus,
    DEFAULT_TEAM_SORT,
    filterMembers,
    nextTeamSort,
    sortMembers,
    STATUS_FILTERS,
    TEAM_NEW_PATH,
    teamMemberPath,
    type TeamSort,
    type TeamStatusFilter
} from "./Team/teamData";

const EMPTY_FILTER_COPY: Record<TeamStatusFilter, string> = {
    All: "No members yet.",
    Active: "No active members.",
    Invited: "No pending invitations.",
    Suspended: "No suspended members."
};

/**
 * Client Console Team page (`/team`). A member-management table in the
 * standard SaaS shape: the page header with Add Member, status tabs with
 * counts and a name/email search, then one row per member (name and email,
 * role, status, last active, added). A row opens the member's own page,
 * `/team/:memberUuid` (`TeamMemberPage`), which holds every action on them;
 * +Add Member opens `/team/new` (`TeamAddMemberPage`).
 *
 * Wired to the live shared org-members API (`useOrgMembers`) against
 * `CLIENT_OAUTH_CONFIG`; React Query invalidation keeps the table current.
 * Self detection comes from the org auth store (`me`); only Owners invite.
 */
export function TeamPage(): React.ReactElement {
    const me = useOrgAuthStore((s) => s.me);
    const myUuid = me?.user.uuid;
    const viewerIsOwner = me?.orgRole === "Owner";

    const { members, isLoading, error, refetch } =
        useOrgMembers(CLIENT_OAUTH_CONFIG);
    const navigate = useNavigate();
    // The page enter rises 8px: movement, so it is skipped under reduced
    // motion (rule: motion/transform-opacity).
    const reducedMotion = useReducedMotion() ?? false;

    const [statusFilter, setStatusFilter] = useState<TeamStatusFilter>("All");
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState<TeamSort>(DEFAULT_TEAM_SORT);

    const counts = useMemo(() => countByStatus(members), [members]);
    const visibleMembers = useMemo(
        () =>
            sortMembers(
                filterMembers(members, { status: statusFilter, query }),
                sort,
                myUuid
            ),
        [members, statusFilter, query, sort, myUuid]
    );

    const rows: MemberRow[] = visibleMembers.map((member) => ({
        member,
        isSelf: member.uuid === myUuid
    }));

    const legalSubtitle =
        me?.org.legalName && me.org.legalName.trim() !== me.org.name.trim()
            ? me.org.legalName
            : undefined;

    const emptyState = (
        <div className="px-4 md:px-6 py-10">
            <Text
                variant="small"
                render={<p />}
                className="text-foreground-muted"
            >
                {query.trim()
                    ? `No members match "${query.trim()}".`
                    : EMPTY_FILTER_COPY[statusFilter]}
            </Text>
        </div>
    );

    return (
        <motion.div
            initial={reducedMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            // Page enter: 400ms, named out-expo (rule: motion/named-easing).
            transition={{ duration: 0.4, ease: easings.outExpo }}
            className="flex flex-col w-full"
        >
            <Heading
                asElement="header"
                title={me ? `${me.org.name} Team` : "Team"}
                // The legal name only when it says something the title does
                // not; many orgs register under their display name.
                subtitle={legalSubtitle}
                className="px-4 md:px-6 pt-3 md:pt-6 pb-3 md:pb-6"
            />

            {isLoading ? (
                <>
                    <TeamControlsPlaceholder />
                    <MembersTableSkeleton />
                </>
            ) : error ? (
                <TeamErrorState
                    // A load failure, not a save: the "team" context words
                    // every server error as "Team update was not saved".
                    message={resolveClientFormError(
                        error,
                        "general",
                        "Refresh and try again."
                    )}
                    onRetry={() => void refetch()}
                />
            ) : (
                <>
                    {/* Controls, one row: status tabs (with counts) and search
                        together on the left (gap-4), Add Member on the right,
                        all vertically centred; stacked on mobile. No baseline
                        rule: the column labels below get the one rule under
                        them, and a rule here would bracket that label row
                        (ui-guide 5g). */}
                    <div className="flex flex-col gap-3 px-4 pb-3 md:flex-row md:items-center md:justify-between md:gap-6 md:px-6">
                        <div className="flex min-w-0 flex-col gap-3 md:flex-1 md:flex-row md:items-center md:gap-4">
                            <TabsMenu
                                value={statusFilter}
                                onValueChange={(value) =>
                                    setStatusFilter(value as TeamStatusFilter)
                                }
                                align="start"
                                className="overflow-x-auto"
                            >
                                {STATUS_FILTERS.map((filter) => (
                                    <TabTrigger
                                        key={filter}
                                        value={filter}
                                        size="sm"
                                    >
                                        {filter}
                                        <Text
                                            variant="number"
                                            render={<span />}
                                            className="ml-1.5 tabular-nums text-foreground-muted"
                                        >
                                            ({counts[filter]})
                                        </Text>
                                    </TabTrigger>
                                ))}
                            </TabsMenu>
                            <div className="md:w-80 md:min-w-40 md:shrink">
                                <InputGroup>
                                    <InputGroupAddon>
                                        <SearchIcon
                                            aria-hidden="true"
                                            className="size-4"
                                        />
                                    </InputGroupAddon>
                                    <InputGroupInput
                                        type="search"
                                        aria-label="Search members by name or email"
                                        placeholder="Search name or email"
                                        value={query}
                                        onChange={(e) =>
                                            setQuery(e.target.value)
                                        }
                                    />
                                </InputGroup>
                            </div>
                        </div>
                        {viewerIsOwner && (
                            <Button
                                variant="raised"
                                interaction="lifted"
                                size="sm"
                                className="shrink-0 self-start whitespace-nowrap md:self-auto"
                                onClick={() => navigate(TEAM_NEW_PATH)}
                            >
                                +Add Member
                            </Button>
                        )}
                    </div>

                    <MembersTable
                        rows={rows}
                        sort={sort}
                        onSort={(column) =>
                            setSort((current) => nextTeamSort(current, column))
                        }
                        onOpen={(uuid) => navigate(teamMemberPath(uuid))}
                        empty={emptyState}
                    />
                </>
            )}
        </motion.div>
    );
}

/** Loading stand-in for the tabs + search row, same construction, no rule. */
function TeamControlsPlaceholder(): React.ReactElement {
    return (
        <div
            aria-hidden="true"
            className="flex flex-col gap-3 px-4 pb-3 md:flex-row md:items-center md:gap-4 md:px-6"
        >
            <div className="flex gap-4 py-2">
                {["w-10", "w-14", "w-16", "w-20"].map((width) => (
                    <Skeleton
                        key={width}
                        className={`h-5 ${width} rounded-none`}
                    />
                ))}
            </div>
            <div className="md:w-80">
                <Skeleton className="h-8 w-full rounded-none" />
            </div>
        </div>
    );
}

function TeamErrorState({
    message,
    onRetry
}: {
    message: string;
    onRetry: () => void;
}): React.ReactElement {
    return (
        // Separated from the page header by whitespace, not a rule; the
        // gutter sits on this single-child wrapper (ui-guide 5g).
        <section className="px-4 md:px-6 pt-4 pb-10">
            <div className="flex flex-col gap-4">
                <Heading
                    title="Team could not load"
                    titleVariant="h2"
                    subtitle={message}
                    subtitleVariant="body"
                    className="gap-3 [&_p]:max-w-prose"
                />
                <Button
                    variant="raised"
                    interaction="lifted"
                    size="md"
                    onClick={onRetry}
                    className="self-start"
                >
                    Retry
                </Button>
            </div>
        </section>
    );
}
